/**
 * The day an election signed today would take effect.
 *
 * A second home for a rule that already lives in `rust/trading-api/src/election.rs`, and
 * deliberately so: the confirmation has to name the date *before* the election is made, and the
 * only other way to learn it is to make one. The server stays authoritative — its response
 * carries the date it actually recorded — so the worst this can do is preview a day that the
 * response then corrects, never change what is stored.
 *
 * `election.rs` has the same cases pinned in its own tests: the day after signing, never the day
 * itself.
 */
export function effectiveFrom(today: Date): Date {
  // Not the same day: the day in progress has already accrued Amps the daily job has not written
  // yet, so making it a USD day is the one case that can pay for one day twice. This was the next
  // Monday until 2026-08-13 — the sweep turned out to handle a week split between the two
  // currencies day by day, so the wait bought nothing but a later start.
  return new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() + 1));
}

/** `YYYY-MM-DD`, matching what the server stores and returns. */
export function effectiveFromIso(today: Date): string {
  return effectiveFrom(today).toISOString().slice(0, 10);
}

/**
 * When the switch actually happens, said in the reader's own time.
 *
 * The ledger keys days in UTC and the server returns `effective_from` as a bare `YYYY-MM-DD`.
 * Printing that date as-is was a UTC day shown without saying so, and it misleads in both
 * directions west of Greenwich: in Bogotá `2026-08-14` begins at 7pm on the *13th*, so an
 * affiliate clicking at 6:55pm read "starts the 14th", reasonably heard "tomorrow", and had it
 * take effect five minutes later. Ten minutes later the same affiliate would read "starts the
 * 15th" — two days off their calendar — for a switch a day away.
 *
 * So the instant is what is rendered, not the date: 00:00 UTC on the effective day, formatted in
 * the viewer's zone with the zone named. `Aug 13, 7:00 PM GMT-5` answers the question the bare
 * date could not, and needs no sentence explaining which calendar it belongs to.
 *
 * `timeZone` is for tests, which must not depend on the machine they run on. Left undefined in the
 * app so `Intl` resolves the viewer's.
 */
export function formatElectionStart(
  effectiveFromDate: string,
  locale: string,
  timeZone?: string,
): string {
  // `YYYY-MM-DD` parses as UTC midnight; appending `T00:00:00` without a `Z` would parse as *local*
  // midnight and shift the answer by the offset — the very error this function exists to prevent.
  const startsAt = new Date(`${effectiveFromDate}T00:00:00Z`);
  if (Number.isNaN(startsAt.getTime())) return effectiveFromDate;

  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
    timeZone,
  }).format(startsAt);
}
